import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';

const ItemEditor = ({ selectedItem, onUpdate }) => {
  const [parLevel, setParLevel] = useState('');
  const [replenishmentMin, setReplenishmentMin] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (selectedItem) {
      setParLevel(selectedItem.par_level || '');
      setReplenishmentMin(selectedItem.replenishment_minimum_qty ?? '');
      setNotes(selectedItem.notes || '');
    } else {
      setParLevel('');
      setReplenishmentMin('');
      setNotes('');
    }
  }, [selectedItem]);

  const handleSave = async () => {
    if (selectedItem && onUpdate) {
      const trimmed = String(replenishmentMin ?? '').trim();
      const parsed = trimmed === '' ? null : Number.parseFloat(trimmed);
      const replenishmentQty = Number.isFinite(parsed) ? parsed : null;

      // Persist the replenishment floor on the inventory master. This is an
      // item-level replenishment policy in the item's count unit — not a
      // vendor minimum (those live on purchase_options).
      const inventoryItemId = selectedItem.inventory_item_id || selectedItem.id;
      if (inventoryItemId) {
        const { error } = await supabase
          .from('inventory_items')
          .update({ replenishment_minimum_qty: replenishmentQty })
          .eq('id', inventoryItemId);
        // Tolerate a database without the column yet (42703 / PGRST204):
        // that was the only field in this write, so "retry without it" is a
        // no-op — the PAR/notes save below still proceeds.
        if (error && error.code !== '42703' && error.code !== 'PGRST204') {
          console.error('Error saving replenishment minimum:', error);
        }
      }

      onUpdate(selectedItem.id, {
        par_level: parLevel,
        replenishment_minimum_qty: replenishmentQty,
        notes: notes
      });
    }
  };

  if (!selectedItem) {
    return (
      <div className="text-center py-5">
        <div className="text-muted mb-3">
          <i className="feather icon-edit" style={{ fontSize: '3rem' }}></i>
        </div>
        <h6 className="text-muted">Select an Item</h6>
        <p className="small text-muted">Choose an item from the list to edit PAR and replenishment values</p>
      </div>
    );
  }

  const snap = selectedItem.item_snapshot || selectedItem;
  const description = snap.description || selectedItem.description || 'Unknown Item';
  const brand = snap.brand || selectedItem.brand;
  const vendor = snap.vendor || selectedItem.vendor;
  const category = snap.category || selectedItem.category;
  const location = selectedItem.location || snap.location;
  const unit = selectedItem.par_unit || snap.unit || 'each';

  return (
    <div className="h-100">
      {/* Item Header */}
      <div className="mb-4">
        <h6 className="fw-bold text-primary mb-2">
          <i className="feather icon-edit me-2"></i>
          Edit Item
        </h6>
        <div className="card border-0 bg-light">
          <div className="card-body p-3">
            <h6 className="fw-bold mb-2">{description}</h6>
            <div className="row text-muted small">
              {brand && (
                <div className="col-6 mb-1">
                  <i className="feather icon-tag me-1"></i>
                  <strong>Brand:</strong> {brand}
                </div>
              )}
              {vendor && (
                <div className="col-6 mb-1">
                  <i className="feather icon-truck me-1"></i>
                  <strong>Vendor:</strong> {vendor}
                </div>
              )}
              {category && (
                <div className="col-6 mb-1">
                  <i className="feather icon-folder me-1"></i>
                  <strong>Category:</strong> {category}
                </div>
              )}
              {location && (
                <div className="col-6 mb-1">
                  <i className="feather icon-map-pin me-1"></i>
                  <strong>Location:</strong> {location}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PAR Level */}
      <div className="mb-4">
        <label className="form-label fw-bold">
          <i className="feather icon-target me-2 text-warning"></i>
          PAR Level
        </label>
        <div className="input-group">
          <input
            type="number"
            className="form-control"
            step="0.25"
            min="0"
            value={parLevel}
            onChange={(e) => setParLevel(e.target.value)}
            placeholder="Enter PAR level"
          />
          <span className="input-group-text">{unit}</span>
        </div>
        <small className="text-muted">
          <i className="feather icon-info me-1"></i>
          Minimum quantity to maintain in inventory
        </small>
      </div>

      {/* Replenishment minimum */}
      <div className="mb-4">
        <label className="form-label fw-bold">
          <i className="feather icon-shopping-cart me-2 text-info"></i>
          Replenishment minimum
        </label>
        <div className="input-group">
          <input
            type="number"
            className="form-control"
            step="0.25"
            min="0"
            value={replenishmentMin}
            onChange={(e) => setReplenishmentMin(e.target.value)}
            placeholder="Optional"
          />
          <span className="input-group-text">{unit}</span>
        </div>
        <small className="text-muted">
          <i className="feather icon-info me-1"></i>
          Optional floor for orders, in the item's count unit — not a vendor minimum. Vendor minimums live on each vendor product.
        </small>
      </div>

      {/* Notes */}
      <div className="mb-4">
        <label className="form-label fw-bold">
          <i className="feather icon-file-text me-2"></i>
          Notes
        </label>
        <textarea
          className="form-control"
          rows="3"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this item..."
        />
      </div>

      {/* Actions */}
      <div className="d-flex gap-2">
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={!selectedItem}
        >
          <i className="feather icon-save me-1"></i>
          Save Changes
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => {
            setParLevel(selectedItem?.par_level || '');
            setReplenishmentMin(selectedItem?.replenishment_minimum_qty ?? '');
            setNotes(selectedItem?.notes || '');
          }}
        >
          <i className="feather icon-refresh-cw me-1"></i>
          Reset
        </button>
      </div>

      {/* Quick Actions */}
      <div className="mt-4 pt-3 border-top">
        <h6 className="fw-bold text-muted mb-2">Quick Actions</h6>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-outline-warning btn-sm"
            onClick={() => setParLevel('0')}
          >
            Zero PAR
          </button>
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() => setParLevel('10')}
          >
            Standard (PAR 10)
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemEditor;
